import * as Sentry from '@sentry/react'
import { lazy } from 'react'

import { useAuth } from 'src/auth'
import TrainingGate from 'src/components/TrainingGate/TrainingGate'
import { useIntercomUrlUpdates } from 'src/hooks/useIntercomUrlUpdates'
import Agent2DashboardLayout from 'src/layouts/Agent2DashboardLayout/Agent2DashboardLayout'
import App2DashboardLayout from 'src/layouts/App2DashboardLayout/App2DashboardLayout'
import ApplicantNavigationLayout from 'src/layouts/ApplicantNavigationLayout/ApplicantNavigationLayout'
import ApplicantSessionLayout from 'src/layouts/ApplicantSessionLayout/ApplicantSessionLayout'
import BusinessPortalLayout from 'src/layouts/BusinessPortalLayout/BusinessPortalLayout'
import ProfessionalDashboardLayout from 'src/layouts/ProfessionalDashboardLayout/ProfessionalDashboardLayout'
import { PrivateSet, Route, Router, Set } from 'src/platform/router'
import { lazyRouteLayout } from 'src/platform/router/lazyRouteLayout'

import { milano } from './utils/milano'

// Legacy MUI layouts load only when their route is entered.
const AdminContainerLayout = lazyRouteLayout(() => import('src/layouts/AdminContainerLayout/AdminContainerLayout'))
const AdminDashboardLayout = lazyRouteLayout(() => import('src/layouts/AdminDashboardLayout/AdminDashboardLayout'))
const ApplicantLayout = lazyRouteLayout(() => import('src/layouts/ApplicantLayout/ApplicantLayout'))
const ClientContainerLayout = lazyRouteLayout(() => import('src/layouts/ClientContainerLayout/ClientContainerLayout'))
const ClientDashboardLayout = lazyRouteLayout(() => import('src/layouts/ClientDashboardLayout/ClientDashboardLayout'))
const PublicLayoutMd = lazyRouteLayout(() => import('src/layouts/PublicLayout/PublicLayout').then((module) => ({ default: module.PublicLayoutMd })))
const PublicLayoutFull = lazyRouteLayout(() => import('src/layouts/PublicLayout/PublicLayout').then((module) => ({ default: module.PublicLayoutFull })))

// The pages. Cedar's auto-loader used to declare these from the directory
// listing; since Cedar removal Stage 3.4 they are spelled out below, one
// `lazy()` per page, so each page stays its own chunk loaded on first
// navigation. A page is any `*Page.tsx` under src/pages; the identifier is
// its directory path without slashes. To add a page, add its directory and
// one line to the list. FatalErrorPage is App.tsx's, imported statically.
const YardiSsnCorrectionGuidePage = lazy(() => import('src/pages/YardiSsnCorrectionGuidePage/YardiSsnCorrectionGuidePage'))
const A2ApplicationsPage = lazy(() => import('src/pages/A2ApplicationsPage/A2ApplicationsPage'))
const AccessPage = lazy(() => import('src/pages/AccessPage/AccessPage'))
const Agent2Page = lazy(() => import('src/pages/Agent2Page/Agent2Page'))
const AgentPage = lazy(() => import('src/pages/AgentPage/AgentPage'))
const App2Page = lazy(() => import('src/pages/App2Page/App2Page'))
const App2WorklistPage = lazy(() => import('src/pages/App2WorklistPage/App2WorklistPage'))
const AppConclusionsPage = lazy(() => import('src/pages/AppConclusionsPage/AppConclusionsPage'))
const AppPage = lazy(() => import('src/pages/AppPage/AppPage'))
const AppfolioTesterPage = lazy(() => import('src/pages/AppfolioTesterPage/AppfolioTesterPage'))
const ApplicantPage = lazy(() => import('src/pages/ApplicantPage/ApplicantPage'))
const ApplicantPickerPage = lazy(() => import('src/pages/ApplicantPickerPage/ApplicantPickerPage'))
const ApplicantRedirectPage = lazy(() => import('src/pages/ApplicantRedirectPage/ApplicantRedirectPage'))
const ApplicationsIndexPage = lazy(() => import('src/pages/ApplicationsIndexPage/ApplicationsIndexPage'))
const ApplyLandingPage = lazy(() => import('src/pages/ApplyLandingPage/ApplyLandingPage'))
const BacktestManagementPage = lazy(() => import('src/pages/BacktestManagementPage/BacktestManagementPage'))
const BacktestSetupPage = lazy(() => import('src/pages/BacktestSetupPage/BacktestSetupPage'))
const BankLinkTestPage = lazy(() => import('src/pages/BankLinkTestPage/BankLinkTestPage'))
const BonusGradingAdminPage = lazy(() => import('src/pages/BonusGradingAdminPage/BonusGradingAdminPage'))
const BonusGradingPage = lazy(() => import('src/pages/BonusGradingPage/BonusGradingPage'))
const BusinessPortalPage = lazy(() => import('src/pages/BusinessPortalPage/BusinessPortalPage'))
const ChangeUnitPage = lazy(() => import('src/pages/ChangeUnitPage/ChangeUnitPage'))
const ChatGradingAdminPage = lazy(() => import('src/pages/ChatGradingAdminPage/ChatGradingAdminPage'))
const ChatGradingPage = lazy(() => import('src/pages/ChatGradingPage/ChatGradingPage'))
const ChatPage = lazy(() => import('src/pages/ChatPage/ChatPage'))
const CommsPage = lazy(() => import('src/pages/CommsPage/CommsPage'))
const CompliancePage = lazy(() => import('src/pages/CompliancePage/CompliancePage'))
const ConsumerReportPage = lazy(() => import('src/pages/ConsumerReportPage/ConsumerReportPage'))
const ConversationMetaGradingAdminPage = lazy(() => import('src/pages/ConversationMetaGradingAdminPage/ConversationMetaGradingAdminPage'))
const ConversationMetaGradingPage = lazy(() => import('src/pages/ConversationMetaGradingPage/ConversationMetaGradingPage'))
const CreateApplyLinkPage = lazy(() => import('src/pages/CreateApplyLinkPage/CreateApplyLinkPage'))
const CsEmailsPage = lazy(() => import('src/pages/CsEmailsPage/CsEmailsPage'))
const DebugPolicyEditPage = lazy(() => import('src/pages/DebugPolicyEditPage/DebugPolicyEditPage'))
const DebugPolicyPage = lazy(() => import('src/pages/DebugPolicyPage/DebugPolicyPage'))
const DebugSettingsStorePage = lazy(() => import('src/pages/DebugSettingsStorePage/DebugSettingsSyncPage'))
const DemoPage = lazy(() => import('src/pages/DemoPage/DemoPage'))
const DisputePage = lazy(() => import('src/pages/DisputePage/DisputePage'))
const DocumentPage = lazy(() => import('src/pages/DocumentPage/DocumentPage'))
const DocumentRetrievalPage = lazy(() => import('src/pages/DocumentRetrievalPage/DocumentRetrievalPage'))
const DojoQuestionAdminPage = lazy(() => import('src/pages/DojoQuestionAdminPage/DojoQuestionAdminPage'))
const DojoQuestionCollectionPage = lazy(() => import('src/pages/DojoQuestionCollectionPage/DojoQuestionCollectionPage'))
const DojoQuestionPage = lazy(() => import('src/pages/DojoQuestionPage/DojoQuestionPage'))
const DojoQuestionResultsPage = lazy(() => import('src/pages/DojoQuestionResultsPage/DojoQuestionResultsPage'))
const EntrataCoApplicantInstructionsPage = lazy(() => import('src/pages/EntrataCoApplicantInstructionsPage/EntrataCoApplicantInstructionsPage'))
const EntrataTesterPage = lazy(() => import('src/pages/EntrataTesterPage/EntrataTesterPage'))
const EvePage = lazy(() => import('src/pages/EvePage/EvePage'))
const EvieDemoPage = lazy(() => import('src/pages/EvieDemoPage/EvieDemoPage'))
const EvieFakePhonePage = lazy(() => import('src/pages/EvieFakePhonePage/EvieFakePhonePage'))
const FeatureRequestPage = lazy(() => import('src/pages/FeatureRequestPage/FeatureRequestPage'))
const FirstKeyConsentPage = lazy(() => import('src/pages/FirstKeyConsentPage/FirstKeyConsentPage'))
const FollowUpTrainingAdminPage = lazy(() => import('src/pages/FollowUpTrainingAdminPage/FollowUpTrainingAdminPage'))
const FollowUpTrainingPage = lazy(() => import('src/pages/FollowUpTrainingPage/FollowUpTrainingPage'))
const FollowupsChatHelpPage = lazy(() => import('src/pages/FollowupsChatHelpPage/FollowupsChatHelpPage'))
const ForensicsPage = lazy(() => import('src/pages/ForensicsPage/ForensicsPage'))
const GraderAccuracyDashboardPage = lazy(() => import('src/pages/GraderAccuracyDashboardPage/GraderAccuracyDashboardPage'))
const GuestCardDetailPage = lazy(() => import('src/pages/GuestCardDetailPage/GuestCardDetailPage'))
const GuestCardsPage = lazy(() => import('src/pages/GuestCardsPage/GuestCardsPage'))
const HomePage = lazy(() => import('src/pages/HomePage/HomePage'))
const InspectPmsPage = lazy(() => import('src/pages/InspectPmsPage/InspectPmsPage'))
const InstitutionCoveragePage = lazy(() => import('src/pages/InstitutionCoveragePage/InstitutionCoveragePage'))
const IntegrationPage = lazy(() => import('src/pages/IntegrationPage/IntegrationPage'))
const InternalSettingsPage = lazy(() => import('src/pages/InternalSettingsPage/InternalSettingsPage'))
const InternalXrayPage = lazy(() => import('src/pages/InternalXrayPage/InternalXrayPage'))
const InviteRedirectPage = lazy(() => import('src/pages/InviteRedirectPage/InviteRedirectPage'))
const LegacyRedirectPage = lazy(() => import('src/pages/LegacyRedirectPage/LegacyRedirectPage'))
const ListPage = lazy(() => import('src/pages/ListPage/ListPage'))
const LoginPage = lazy(() => import('src/pages/LoginPage/LoginPage'))
const ManualCreditPage = lazy(() => import('src/pages/ManualCreditPage/ManualCreditPage'))
const ManualIngestPage = lazy(() => import('src/pages/ManualIngestPage/ManualIngestPage'))
const ManualReportsTreePage = lazy(() => import('src/pages/ManualReportsTreePage/ManualReportsTreePage'))
const MetricsRedirectPage = lazy(() => import('src/pages/MetricsRedirectPage/MetricsRedirectPage'))
const AffordableCentralizedMonitorPage = lazy(() => import('src/pages/AffordableCentralizedMonitorPage/AffordableCentralizedMonitorPage'))
const MgCentralizedMonitorPage = lazy(() => import('src/pages/MgCentralizedMonitorPage/MgCentralizedMonitorPage'))
const MossTgMonitorPage = lazy(() => import('src/pages/MossTgMonitorPage/MossTgMonitorPage'))
const MyConversationsPage = lazy(() => import('src/pages/MyConversationsPage/MyConversationsPage'))
const MyMetaGradingFeedbackPage = lazy(() => import('src/pages/MyMetaGradingFeedbackPage/MyMetaGradingFeedbackPage'))
const MyReportsPage = lazy(() => import('src/pages/MyReportsPage/MyReportsPage'))
const NotFoundPage = lazy(() => import('src/pages/NotFoundPage/NotFoundPage'))
const NoticesPage = lazy(() => import('src/pages/NoticesPage/NoticesPage'))
const OagOfferPage = lazy(() => import('src/pages/OagOfferPage/OagOfferPage'))
const OfferLetterVerificationPage = lazy(() => import('src/pages/OfferLetterVerificationPage/offerLetterVerificationPage'))
const OwnerEmailReviewPage = lazy(() => import('src/pages/OwnerEmailReviewPage/OwnerEmailReviewPage'))
const OwnerMetricsReportPage = lazy(() => import('src/pages/OwnerMetricsReportPage/OwnerMetricsReportPage'))
const PmConfigPage = lazy(() => import('src/pages/PmConfigPage/PmConfigPage'))
const PmOnboardingAdminPage = lazy(() => import('src/pages/PmOnboardingAdminPage/PmOnboardingAdminPage'))
const PmOnboardingSetupPage = lazy(() => import('src/pages/PmOnboardingSetupPage/PmOnboardingSetupPage'))
const PoliciesPage = lazy(() => import('src/pages/PoliciesPage/PoliciesPage'))
const PortalAgentActivityPage = lazy(() => import('src/pages/PortalAgentActivityPage/PortalAgentActivityPage'))
const PortalAgentPage = lazy(() => import('src/pages/PortalAgentPage/PortalAgentPage'))
const PortalApplicationsPage = lazy(() => import('src/pages/PortalApplicationsPage/PortalApplicationsPage'))
const PortalCompliancePage = lazy(() => import('src/pages/PortalCompliancePage/PortalCompliancePage'))
const CorporateScreeningsPage = lazy(() => import('src/pages/CorporateScreeningsPage/CorporateScreeningsPage'))
const PortalLegalPage = lazy(() => import('src/pages/PortalLegalPage/PortalLegalPage'))
const PortalMetricsPage = lazy(() => import('src/pages/PortalMetricsPage/PortalMetricsPage'))
const PortalMonitorPage = lazy(() => import('src/pages/PortalMonitorPage/PortalMonitorPage'))
const PortalTrainingPage = lazy(() => import('src/pages/PortalTrainingPage/PortalTrainingPage'))
const NonqmAccountingPage = lazy(() => import('src/pages/NonqmAccountingPage/NonqmAccountingPage'))
const PricingExportPage = lazy(() => import('src/pages/PricingExportPage/PricingExportPage'))
const PricingsPage = lazy(() => import('src/pages/PricingsPage/PricingsPage'))
const ProcessTrackerDevPage = lazy(() => import('src/pages/ProcessTrackerDevPage/ProcessTrackerDevPage'))
const PropertiesPage = lazy(() => import('src/pages/PropertiesPage/PropertiesPage'))
const PropertyCodeLookupPage = lazy(() => import('src/pages/PropertyCodeLookupPage/PropertyCodeLookupPage'))
const PropertyListsPage = lazy(() => import('src/pages/PropertyListsPage/PropertyListsPage'))
const PropertyManagerHelpPage = lazy(() => import('src/pages/PropertyManagerHelpPage/PropertyManagerHelpPage'))
const ProspectPage = lazy(() => import('src/pages/ProspectPage/ProspectPage'))
const RentCafeApplyPage = lazy(() => import('src/pages/RentCafeApplyPage/RentCafeApplyPage'))
const RentCafeCoApplicantInstructionsPage = lazy(() => import('src/pages/RentCafeCoApplicantInstructionsPage/RentCafeCoApplicantInstructionsPage'))
const RentCafeFakePage = lazy(() => import('src/pages/RentCafeFakePage/RentCafeFakePage'))
const RentCafeSetupPage = lazy(() => import('src/pages/RentCafeSetupPage/RentCafeSetupPage'))
const RentCafeTesterPage = lazy(() => import('src/pages/RentCafeTesterPage/RentCafeTesterPage'))
const ReportPage = lazy(() => import('src/pages/ReportPage/ReportPage'))
const RpmCentralizedMonitorPage = lazy(() => import('src/pages/RpmCentralizedMonitorPage/RpmCentralizedMonitorPage'))
const SeedPage = lazy(() => import('src/pages/SeedPage/SeedPage'))
const SelfDenialSurveyPage = lazy(() => import('src/pages/SelfDenialSurveyPage/SelfDenialSurveyPage'))
const SeniorPersonnelPage = lazy(() => import('src/pages/SeniorPersonnelPage/SeniorPersonnelPage'))
const SippTesterPage = lazy(() => import('src/pages/SippTesterPage/SippTesterPage'))
const SplitterPage = lazy(() => import('src/pages/SplitterPage/SplitterPage'))
const SupportBotTrainingAdminPage = lazy(() => import('src/pages/SupportBotTrainingAdminPage/SupportBotTrainingAdminPage'))
const SupportBotTrainingPage = lazy(() => import('src/pages/SupportBotTrainingPage/SupportBotTrainingPage'))
const TaskerBadgePage = lazy(() => import('src/pages/TaskerBadgePage/TaskerBadgePage'))
const TeamAccessPage = lazy(() => import('src/pages/TeamAccessPage/TeamAccessPage'))
const TodoPage = lazy(() => import('src/pages/TodoPage/TodoPage'))
const UnitSearchPage = lazy(() => import('src/pages/UnitSearchPage/UnitSearchPage'))
const XrayPage = lazy(() => import('src/pages/XrayPage/XrayPage'))

const Routes = () => {
  const { isAuthenticated, currentUser, error } = useAuth()
  useIntercomUrlUpdates(isAuthenticated)
  if (error) {
    console.error('auth error', error)
  }
  if (isAuthenticated) {
    const roles = currentUser?.roles as string[]
    const isSuper = roles?.includes('Super')
    const isTasker = roles?.includes('Tasker')
    const isApplicant = roles?.includes('Applicant')
    const isAgent = roles?.includes('Agent')
    const intercomRole = (() => {
      if (isSuper) return 'Super'
      if (isTasker) return 'Tasker'
      if (isAgent) return 'Agent'
      if (isApplicant) return 'Applicant'
    })()
    window.Intercom('boot', {
      api_base: 'https://api-iam.intercom.io',
      app_id: process.env.TWODOTS_ENV === 'prod' ? 'dqkyw6ws' : 'ca9ahjig',
      user_id: currentUser?.id,
      user_hash: currentUser?.intercomHmac,
      email: currentUser?.email,
      created_at: currentUser?.createdAt,
      custom_launcher_selector: '.launch-intercom',
      hide_default_launcher: false, // Default: show launcher
      role: intercomRole,
      milano: milano(),
    })
  }
  if (window.location.hostname.startsWith('app.')) {
    if (currentUser?.email && typeof currentUser?.email === 'string') {
      Sentry.setUser({
        email: currentUser?.email,
        ip_address: '{{auto}}',
      })
      const rolesSerializedString = currentUser?.roles && Array.isArray(currentUser?.roles) ? currentUser?.roles.map((role) => String(role)).join(',') : ''
      Sentry.setTags({
        ['twodots.user.roles']: rolesSerializedString,
      })
    }

    Sentry.setTags({
      ['twodots.milano']: milano(),
    })
  }
  return (
    <Router useAuth={useAuth} trailingSlashes="preserve">
      <Route path="/report/{propertyManagerName:String}/code/{prospectCode:String}" page={ReportPage} name="report" />

      {/* Tasker routes - AdminContainerLayout for Dojo, Prospect */}
      <PrivateSet unauthenticated="login" roles="Tasker" wrap={AdminContainerLayout}>
        <Route path="/dojo/{id:Int}" page={DojoQuestionCollectionPage} name="dojo" />
        <Route path="/dojo/results" page={DojoQuestionResultsPage} name="dojoResults" />
        <Route path="/dojo/question/{dojoQuestionId:Int}" page={DojoQuestionPage} name="dojoQuestion" />
        <Route path="/prospect/{code:String}" page={ProspectPage} name="prospect" />
      </PrivateSet>

      {/* Tasker routes - AdminDashboardLayout for full-width internal pages */}
      <PrivateSet unauthenticated="login" roles="Tasker" wrap={AdminDashboardLayout}>
        <Route path="/my-reports" page={MyReportsPage} name="myReports" />
        <Route path="/my-conversations" page={MyConversationsPage} name="myConversations" />
        <Route path="/my-grading-feedback" page={MyMetaGradingFeedbackPage} name="myGradingFeedback" />
        <Route path="/label/{labelType:String}/{mode:String}/{batchId:Int}/tasker/{taskerId:Int}/document/{id:Int}" page={SplitterPage} name="label" />
        <Route path="/chat/{conversationId:Int}" page={ChatPage} name="chat" />
        <Route path="/app/{applicationId:Int}" page={AppPage} name="app" />
        <Route path="/app/{applicationId:Int}/assistant-conversations" redirect="app2CaseworkEvan" />
        <Route path="/apl/{applicantId:Int}" page={ApplicantRedirectPage} name="applicantRedirect" />
        <Route path="/todo" page={TodoPage} name="todo" />
      </PrivateSet>

      {/* Agent/Super routes - AdminDashboardLayout for internal full-width pages */}
      <PrivateSet unauthenticated="login" roles={['Agent', 'Super']} wrap={AdminDashboardLayout}>
        <Route path="/list/{pm:String}" page={ListPage} name="list" />
        <Route path="/list" page={ListPage} name="listByHref" />
        <Route path="/compliance/{pm:String}" page={CompliancePage} name="compliance" />
        <Route path="/guest-cards" page={GuestCardsPage} name="guestCards" />
        <Route path="/guest-cards/{shortCode:String}" page={GuestCardDetailPage} name="guestCardByShortCode" />
        <Route path="/guest-card/{id:String}" page={GuestCardDetailPage} name="guestCardDetail" />
      </PrivateSet>

      {/* Legacy PII routes - training-gated so untrained agents can't deep-link
        applicant PII. Same layout as above; TrainingGate is innermost. */}
      <PrivateSet unauthenticated="login" roles={['Agent', 'Super']} wrap={[AdminDashboardLayout, TrainingGate]}>
        <Route path="/a/{applicationId:Int}" page={AgentPage} name="agentPage" />
        <Route path="/xray/{documentId:Int}" page={XrayPage} name="xray" />
        <Route path="/comms/{applicantId:Int}" page={CommsPage} name="comms" />
      </PrivateSet>

      {/* Agent/Super routes - ClientContainerLayout for client-facing contained pages */}
      <PrivateSet unauthenticated="login" roles={['Agent', 'Super']} wrap={ClientContainerLayout}>
        <Route path="/ingest" page={ManualIngestPage} name="ingest" />
        <Route path="/ingest-evie" page={ManualIngestPage} name="ingestEvie" />
      </PrivateSet>

      {/* Agent/Super routes - AdminContainerLayout for contained internal tools */}
      <PrivateSet unauthenticated="login" roles={['Super', 'Tasker', 'Admin']} wrap={AdminContainerLayout}>
        <Route path="/institution-coverage" page={InstitutionCoveragePage} name="institutionCoverage" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles={['Admin', 'Super']} wrap={AdminContainerLayout}>
        <Route path="/debug/settings-sync" page={DebugSettingsStorePage} name="debugSettingsStore" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles={['Admin']} wrap={BusinessPortalLayout}>
        <Route path="/portal/{pm:slug}/debug" page={DebugPolicyPage} name="debugPolicies" />
        <Route path="/portal/{pm:slug}/policies/debug/edit/{policyId:Int}" page={DebugPolicyEditPage} name="debugPolicyEdit" />
        <Route path="/portal/{pm:slug}/policies/policy/{policyId:Int}/admin" page={DebugPolicyEditPage} name="policyAdmin" />
        <Route path="/portal/{pm:slug}/policies/policy/{policyId:String}/debug" page={DebugPolicyPage} name="debugPolicyDetail" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles={['Agent', 'Admin', 'Super']} wrap={Agent2DashboardLayout}>
        <Route path="/a2" page={A2ApplicationsPage} name="agent2Applications" />
        <Route path="/a2-evie" page={A2ApplicationsPage} name="agent2EvieApplications" />
      </PrivateSet>

      {/* Agent2Cell resolves existing /a2 links to the canonical route for
        each application’s enrollment. */}
      <PrivateSet unauthenticated="login" roles={['Agent', 'Super']} wrap={Agent2DashboardLayout}>
        <Route path="/a2/{applicationId:Int}" page={Agent2Page} name="agent2Page" />
        <Route path="/a2/{applicationId:Int}/applicant/{applicantId:Int}" page={Agent2Page} name="agent2ApplicantDetail" />
        <Route path="/a2-evie/{applicationId:Int}" page={Agent2Page} name="agent2EviePage" />
        <Route path="/a2-evie/{applicationId:Int}/applicant/{applicantId:Int}" page={Agent2Page} name="agent2EvieApplicantDetail" />
      </PrivateSet>

      {/* app2 - the Evie ops cockpit (worklist + case pages). Admin-only
        to match the evieAdmin/app2 SDLs it drives; App2DashboardLayout is
        a dedicated Professional shell with shared React Aria chrome. The
        remaining casework/agent controls use its explicit retirement boundary.

        The case routes are one page: the same lazy App2Page, so the
        router reconciles them as one element and the Sidekick's in-flight
        turn survives moving between the overview, casework, evidence and
        applicant views. Keep them on App2Page — a second page component
        here would remount the Evie Operator panel on every sidebar click. */}
      <PrivateSet unauthenticated="login" roles={['Admin']} wrap={App2DashboardLayout}>
        <Route path="/app2" page={App2WorklistPage} name="app2Worklist" />
        <Route path="/app2/{applicationId:Int}" page={App2Page} name="app2" />
        <Route path="/app2/{applicationId:Int}/evidence" page={App2Page} name="app2Evidence" />
        <Route path="/app2/{applicationId:Int}/casework" page={App2Page} name="app2Casework" />
        <Route path="/app2/{applicationId:Int}/casework/timeline" page={App2Page} name="app2CaseworkTimeline" />
        <Route path="/app2/{applicationId:Int}/casework/fraud" page={App2Page} name="app2CaseworkFraud" />
        <Route path="/app2/{applicationId:Int}/casework/documents" page={App2Page} name="app2CaseworkDocuments" />
        <Route path="/app2/{applicationId:Int}/casework/income" page={App2Page} name="app2CaseworkIncome" />
        <Route path="/app2/{applicationId:Int}/casework/offer-letter" page={App2Page} name="app2CaseworkOfferLetter" />
        <Route path="/app2/{applicationId:Int}/casework/overrides" page={App2Page} name="app2CaseworkOverrides" />
        <Route path="/app2/{applicationId:Int}/casework/identity" page={App2Page} name="app2CaseworkIdentity" />
        <Route path="/app2/{applicationId:Int}/casework/evan" page={App2Page} name="app2CaseworkEvan" />
        <Route path="/app2/{applicationId:Int}/applicant/{applicantId:Int}" page={App2Page} name="app2ApplicantDetail" />
        <Route path="/app2/{applicationId:Int}/applicant/{applicantId:Int}/support" page={App2Page} name="app2ApplicantSupport" />
        <Route path="/app2/{applicationId:Int}/applicant/{applicantId:Int}/info" page={App2Page} name="app2ApplicantInfo" />
        <Route path="/app2/{applicationId:Int}/applicant/{applicantId:Int}/docs" page={App2Page} name="app2ApplicantDocs" />
      </PrivateSet>

      {/* Business Portal - client-themed PM-facing routes. `BusinessPortal`
        is minted from the workspace grant (holding portal access in any
        workspace), so gating on it here admits exactly the people the
        DB-backed permission system says belong. */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Super', 'BusinessPortal']} wrap={BusinessPortalLayout}>
        <Route path="/portal" page={BusinessPortalPage} name="portalHome" />
        <Route path="/portal/policies" page={PoliciesPage} name="portalPoliciesShell" />
        <Route path="/portal/{pm:slug}/policies" page={PoliciesPage} name="portalPolicies" />
        <Route path="/portal/{pm:slug}/policies/policy/{policyId:String}" page={PoliciesPage} name="portalPolicyDetail" />
        <Route path="/portal/properties" page={PropertiesPage} name="portalPropertiesShell" />
        <Route path="/portal/{pm:slug}/properties" page={PropertiesPage} name="portalProperties" />
        <Route path="/portal/{pm:slug}/properties/property/{propertyCode:String}" page={PropertiesPage} name="portalPropertyDetail" />
        <Route path="/portal/pricings" page={PricingsPage} name="portalPricingsShell" />
        <Route path="/portal/{pm:slug}/pricings" page={PricingsPage} name="portalPricings" />
        <Route path="/portal/{pm:slug}/pricings/plan/{planGroupId:String}" page={PricingsPage} name="portalPricingDetail" />
        <Route path="/portal/applications" page={PortalApplicationsPage} name="portalApplicationsShell" />
        <Route path="/portal/{pm:slug}/applications" page={PortalApplicationsPage} name="portalApplications" />
        <Route path="/portal/corporate-screenings" page={CorporateScreeningsPage} name="portalCorporateScreeningsShell" />
        <Route path="/portal/{pm:slug}/corporate-screenings" page={CorporateScreeningsPage} name="portalCorporateScreenings" />
        <Route path="/portal/{pm:slug}/corporate-screenings/corporation/{corporationId:Int}" page={CorporateScreeningsPage} name="portalCorporateScreeningDetail" />
        <Route path="/portal/training" page={PortalTrainingPage} name="portalTrainingShell" />
        <Route path="/portal/{pm:slug}/training" page={PortalTrainingPage} name="portalTraining" />
        <Route path="/portal/notices" page={NoticesPage} name="portalNoticesShell" />
        <Route path="/portal/{pm:slug}/notices" page={NoticesPage} name="portalNotices" />
        <Route path="/portal/team-access" page={TeamAccessPage} name="portalTeamAccessShell" />
        <Route path="/portal/{pm:slug}/team-access" page={TeamAccessPage} name="portalTeamAccess" />
        <Route path="/portal/rentcafe" page={RentCafeSetupPage} name="portalRentcafeShell" />
        <Route path="/portal/{pm:slug}/rentcafe" page={RentCafeSetupPage} name="portalRentcafe" />
        <Route path="/portal/integration" page={IntegrationPage} name="portalIntegrationShell" />
        <Route path="/portal/{pm:slug}/integration" page={IntegrationPage} name="portalIntegration" />
        <Route path="/portal/{pm:slug}/integration/rentcafe" page={RentCafeSetupPage} name="portalIntegrationRentcafe" />
        <Route path="/portal/compliance" page={PortalCompliancePage} name="portalComplianceShell" />
        <Route path="/portal/{pm:slug}/compliance" page={PortalCompliancePage} name="portalCompliance" />
        {/* Legal access is checked per workspace by the page and resolvers. */}
        <Route path="/portal/legal" page={PortalLegalPage} name="portalLegalShell" />
        <Route path="/portal/{pm:slug}/legal" page={PortalLegalPage} name="portalLegal" />
      </PrivateSet>

      {/* Business Portal - Metrics dashboard. Authenticated-only on purpose:
        owner-group users reach their property-list dashboards here and hold
        no portal role, since owner access lives in AuthzAccessEntry rather
        than in a minted role. PortalMetricsPage bounces anyone without a
        grant, and the service layer (assertPmAccess / getPmScope /
        property-list choices) is the real gate per-PM/per-list. */}
      <PrivateSet unauthenticated="login" wrap={BusinessPortalLayout}>
        <Route path="/portal/metrics" page={PortalMetricsPage} name="portalMetricsShell" />
        <Route path="/portal/{pm:slug}/metrics" page={PortalMetricsPage} name="portalMetrics" />
      </PrivateSet>

      {/* Business Portal - per-PM centralized monitor. Route roles match the
        centralizedMonitor SDL; which PMs have a monitor, and whether a PM's
        own members may read it, is enforced by canAccessMonitor here and
        re-derived by the service — the monitor has no permission row of its
        own. Must use the {pm:slug} form so BusinessPortalLayout resolves
        currentPm and doesn't bounce workspace members away. */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Super', 'BusinessPortal']} wrap={BusinessPortalLayout}>
        <Route path="/portal/{pm:slug}/monitor" page={PortalMonitorPage} name="portalRpmMonitor" />
      </PrivateSet>

      {/* Legacy /metrics URLs — bookmarks + the emailed owner links in the
        login redirect allowlist predate the portal move. Public routes on
        purpose: the redirect target's PrivateSet handles auth, so the
        redirectTo chain survives login. */}
      <Route path="/metrics" page={MetricsRedirectPage} name="legacyMetricsShell" />
      <Route path="/metrics/{pm:slug}" page={MetricsRedirectPage} name="legacyMetrics" />
      <Route path="/metrics/{pm:slug}/" page={MetricsRedirectPage} name="legacyMetricsTrailingSlash" />

      {/* Business Portal - Portfolio Strategist agent. The route set MUST
        admit every workspace member (the coarse BusinessPortal role):
        PortalAgentPage's own gate bounces members without agent access to
        the PM's policies page. If the route itself rejected them, Cedar
        would redirect to /?redirectTo=/portal/.../agent, Login's
        portal-regex would navigate straight back, and the user would loop
        forever. Write access is enforced by the agent SDLs' resolvers
        against the workspace grant, not here. */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Super', 'BusinessPortal']} wrap={BusinessPortalLayout}>
        <Route path="/portal/agent" page={PortalAgentPage} name="portalAgentShell" />
        <Route path="/portal/{pm:slug}/agent" page={PortalAgentPage} name="portalAgent" />
        <Route path="/portal/{pm:slug}/agent/chat/{conversationId:String}" page={PortalAgentPage} name="portalAgentConversation" />
        <Route path="/portal/agent/activity" page={PortalAgentActivityPage} name="portalAgentActivityShell" />
        <Route path="/portal/{pm:slug}/agent/activity" page={PortalAgentActivityPage} name="portalAgentActivity" />
      </PrivateSet>

      {/* Owner metrics - ClientDashboardLayout for client-facing full-width pages */}
      <PrivateSet unauthenticated="login" roles="MetricsSender" wrap={ClientDashboardLayout}>
        <Route path="/owner-metrics-report/{pm:String}" page={OwnerMetricsReportPage} name="ownerMetricsReport" />
        <Route path="/owner-email-review" page={OwnerEmailReviewPage} name="ownerEmailReview" />
      </PrivateSet>

      {/* Tasker/Manager/Super routes */}
      <PrivateSet unauthenticated="login" roles={['Tasker', 'Manager', 'Super']} wrap={AdminDashboardLayout}>
        <Route path="/manualReportsTree" page={ManualReportsTreePage} name="manualReportsTree" />
        <Route path="/tasker/badges" page={TaskerBadgePage} name="taskerBadges" />
        <Route path="/senior-personnel" page={SeniorPersonnelPage} name="seniorPersonnel" />
      </PrivateSet>

      {/* Super routes - AdminDashboardLayout for Internal Xray, Forensics */}
      <PrivateSet unauthenticated="login" roles="Super" wrap={AdminDashboardLayout}>
        <Route path="/internal-xray/{documentId:Int}" page={InternalXrayPage} name="internalXray" />
        <Route path="/forensics/{forensicsData:String}" page={ForensicsPage} name="forensics" />
      </PrivateSet>

      {/* Document route - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Tasker', 'Super', 'Agent']} wrap={AdminDashboardLayout}>
        <Route path="/document/{id:Int}" page={DocumentPage} name="document" />
      </PrivateSet>

      {/* Shared applicant selection uses its own Aria shell. */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Super']} wrap={ApplicantNavigationLayout}>
        <Route path="/applications" page={ApplicationsIndexPage} name="applicationsIndex" />
        <Route path="/welcome" page={ApplicationsIndexPage} name="applicationsIndexLegacy" />
        <Route path="/applications/{workflowId:Int}" page={ApplicantPickerPage} name="applicantPicker" />
        <Route path="/workflow/{workflowId:Int}" page={ApplicantPickerPage} name="applicantPickerLegacy" />
      </PrivateSet>

      {/* Applicant routes - ApplicantLayout (client theme with md container) */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Super']} wrap={ApplicantLayout}>
        <Route path="/dispute/{disputeId:Int}" page={DisputePage} name="dispute" />
        <Route path="/rent-cafe-co-applicant-instructions/{coApplicantType:String}/{applicantId:Int}/{workflowId:Int}" page={RentCafeCoApplicantInstructionsPage} name="rentCafeCoApplicantInstructions" />
        <Route path="/entrata-co-applicant-instructions/{coApplicantType:String}/{applicantId:Int}/{workflowId:Int}" page={EntrataCoApplicantInstructionsPage} name="entrataCoApplicantInstructions" />
      </PrivateSet>
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Super']} wrap={ApplicantLayout}>
        <Route path="/help/{propertyManagerName:String}" page={PropertyManagerHelpPage} name="propertyManagerHelp" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/firstKeyConsent" page={FirstKeyConsentPage} name="firstKeyConsent" />
      </PrivateSet>

      {/* Applicant session routes are at the bottom of the router. */}

      {/* Legacy redirect routes - ApplicantLayout */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Agent', 'Super']} wrap={ApplicantLayout}>
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/identityVerification" page={LegacyRedirectPage} name="workflowApplicantIdv" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/consent" page={LegacyRedirectPage} name="workflowApplicantConsent" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/personalInfo" page={LegacyRedirectPage} name="workflowPersonalInfo" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/incomeTypes" page={LegacyRedirectPage} name="workflowApplicantIncomeTypes" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/verified" page={LegacyRedirectPage} name="workflowIncomeVerified" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/exited" page={LegacyRedirectPage} name="workflowExitedAutopilot" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/bank-link" page={LegacyRedirectPage} name="bankLink" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/socialSecurityCard" page={LegacyRedirectPage} name="workflowSocialSecurityCard" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/f1" page={LegacyRedirectPage} name="workflowF1" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/housingVoucher" page={LegacyRedirectPage} name="workflowHousingVoucher" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/offerLetter/{offerLetterType:String}" page={LegacyRedirectPage} name="workflowOfferLetter" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/turboFeedback" page={LegacyRedirectPage} name="workflowTurboFeedback" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/employers" page={LegacyRedirectPage} name="employers" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/survey" page={LegacyRedirectPage} name="incomeTypeSurvey" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/chat" page={LegacyRedirectPage} name="followupsChat" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/self-confirmation-survey" page={SelfDenialSurveyPage} name="selfDenialSurvey" />
        <Route path="/verify/{workflowId:Int}/applicant/{applicantId:Int}/oag-offer" page={OagOfferPage} name="oagOffer" />
        <Route path="/workflow/{workflowId:Int}/applicant/{applicantId:Int}/help" page={FollowupsChatHelpPage} name="followupsChatHelp" />
      </PrivateSet>

      {/* Super routes - AdminContainerLayout for contained internal pages */}
      <PrivateSet unauthenticated="login" roles="Super" wrap={AdminContainerLayout}>
        <Route path="/seed" page={SeedPage} name="seed" />
        <Route path="/evie-demo" page={EvieDemoPage} name="evieDemo" />
        <Route path="/sipp-tester" page={SippTesterPage} name="sippTester" />
        <Route path="/inspect-pms" page={InspectPmsPage} name="inspectPms" />
        <Route path="/entrata-tester" page={EntrataTesterPage} name="entrataTester" />
        <Route path="/appfolio-tester" page={AppfolioTesterPage} name="appfolioTester" />
        <Route path="/rentcafe-tester" page={RentCafeTesterPage} name="rentCafeTester" />
        <Route path="/property-code-lookup" page={PropertyCodeLookupPage} name="propertyCodeLookup" />
        <Route path="/document-retrieval" page={DocumentRetrievalPage} name="documentRetrieval" />
      </PrivateSet>

      {/* Consumer report - restricted to Disputer role */}
      <PrivateSet unauthenticated="login" roles="Disputer" wrap={AdminContainerLayout}>
        <Route path="/consumer-report" page={ConsumerReportPage} name="consumerReport" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles={['Admin', 'Accountant']} wrap={AdminDashboardLayout}>
        <Route path="/nonqm-accounting" page={NonqmAccountingPage} name="nonqmAccounting" />
      </PrivateSet>

      {/* Pricing export - Super + Accountant */}
      <PrivateSet unauthenticated="login" roles={['Super', 'Accountant']} wrap={AdminDashboardLayout}>
        <Route path="/pricing-export" page={PricingExportPage} name="pricingExport" />
      </PrivateSet>

      {/* Super routes - AdminDashboardLayout for full-width internal pages */}
      <PrivateSet unauthenticated="login" roles="Super" wrap={AdminDashboardLayout}>
        <Route path="/cs-emails" page={CsEmailsPage} name="csEmails" />
      </PrivateSet>

      {/* Demo - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Sales']} wrap={AdminDashboardLayout}>
        <Route path="/demo" page={DemoPage} name="demo" />
      </PrivateSet>

      {/* Admin routes - AdminContainerLayout */}
      <PrivateSet unauthenticated="login" roles="Admin" wrap={AdminContainerLayout}>
        <Route path="/app-conclusions/{applicationId:Int}" page={AppConclusionsPage} name="appConclusions" />
        <Route path="/manualCreditCheck" page={ManualCreditPage} name="manualCreditCheck" />
        <Route path="/bank-link-test" page={BankLinkTestPage} name="bankLinkTest" />
        <Route path="/pm-config" page={PmConfigPage} name="pmConfig" />
        <Route path="/internal-settings" page={InternalSettingsPage} name="internalSettings" />
        <Route path="/internal-settings/{pmName:String}" page={InternalSettingsPage} name="internalSettingsPm" />
      </PrivateSet>

      {/* Create Apply Link - Admin and Agent */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Agent']} wrap={AdminContainerLayout}>
        <Route path="/create-apply-link/{pmName:String}" page={CreateApplyLinkPage} name="createApplyLink" />
        <Route path="/unit-search" page={UnitSearchPage} name="unitSearch" />
      </PrivateSet>

      {/* Admin routes - AdminDashboardLayout for full-width pages */}
      <PrivateSet unauthenticated="login" roles="Admin" wrap={AdminDashboardLayout}>
        <Route path="/rpm-centralized-monitor" page={RpmCentralizedMonitorPage} name="rpmCentralizedMonitor" />
        <Route path="/moss-tg-monitor" page={MossTgMonitorPage} name="mossTgMonitor" />
        <Route path="/mg-centralized-monitor" page={MgCentralizedMonitorPage} name="mgCentralizedMonitor" />
        <Route path="/affordable-centralized-monitor" page={AffordableCentralizedMonitorPage} name="affordableCentralizedMonitor" />
        <Route path="/property-lists" page={PropertyListsPage} name="propertyLists" />
      </PrivateSet>

      <PrivateSet unauthenticated="login" roles="Admin" wrap={ProfessionalDashboardLayout}>
        <Route path="/pm-onboarding-admin" page={PmOnboardingAdminPage} name="pmOnboardingAdmin" />
      </PrivateSet>

      {/* PM Onboarding - ClientContainerLayout for client-facing contained pages */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'BusinessPortal']} wrap={ClientContainerLayout}>
        <Route path="/pm-onboarding-setup/{pmName:String}" page={PmOnboardingSetupPage} name="pmOnboardingSetup" />
        <Route path="/pm-onboarding-setup/{pmName:String}/wizard/step/{step:Int}" page={PmOnboardingSetupPage} name="pmOnboardingWizardStep" />
        <Route path="/backtest-management" page={BacktestManagementPage} name="backtestManagement" />
        <Route path="/backtest-setup/{pmName:String}" page={BacktestSetupPage} name="backtestSetup" />
      </PrivateSet>

      {/* Meta-grading routes - AdminDashboardLayout (Manager/Admin only for supervisor-level grading audits) */}
      {/* These must come before the parameterized /grading/conversation/{conversationId:int} route */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Manager']} wrap={AdminDashboardLayout}>
        <Route path="/grading/conversation/accuracy" page={GraderAccuracyDashboardPage} name="conversationGraderAccuracy" />
        <Route path="/grading/conversation/meta/admin" page={ConversationMetaGradingAdminPage} name="conversationMetaGradingAdmin" />
        <Route path="/grading/conversation/meta/{id:int}" page={ConversationMetaGradingPage} name="conversationMetaGrading" />
      </PrivateSet>

      {/* Grading routes - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles={['Super', 'Tasker']} wrap={AdminDashboardLayout}>
        <Route path="/grading" page={BonusGradingPage} name="grading" />
        <Route path="/grading/admin" page={BonusGradingAdminPage} name="gradingAdmin" />
        <Route path="/grading/{userId:int}" page={BonusGradingPage} name="gradingByUserId" />
        <Route path="/grading/conversation/admin" page={ChatGradingAdminPage} name="chatGradingAdmin" />
        {/* Static routes must come before parameterized routes */}
        <Route path="/grading/conversation/{conversationId:int}" page={ChatGradingPage} name="gradingConversation" />
      </PrivateSet>

      {/* Training routes - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles="Admin" wrap={AdminDashboardLayout}>
        <Route path="/support-bot-training" page={SupportBotTrainingAdminPage} name="supportBotTrainingAdmin" />
        <Route path="/support-bot-training/{conversationId:String}" page={SupportBotTrainingPage} name="supportBotTraining" />
        <Route path="/followups-bot-training" page={FollowUpTrainingAdminPage} name="followUpTrainingAdmin" />
        <Route path="/followups-bot-training/{applicantId:Int}" page={FollowUpTrainingPage} name="followUpTraining" />
      </PrivateSet>

      {/* Access page - AdminContainerLayout */}
      <PrivateSet unauthenticated="login" roles="Admin" wrap={AdminContainerLayout}>
        <Route path="/access" page={AccessPage} name="access" />
      </PrivateSet>

      {/* Offer letter verification - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles={['Admin', 'Sales', 'Tasker']} wrap={AdminDashboardLayout}>
        <Route path="/offerLetterVerification/{verificationId:Int}" page={OfferLetterVerificationPage} name="offerLetterVerification" />
      </PrivateSet>

      {/* Other admin routes - AdminContainerLayout */}
      <PrivateSet unauthenticated="login" roles={['Admin']} wrap={AdminContainerLayout}>
        <Route path="/feature-requests" page={FeatureRequestPage} name="featureRequests" />
      </PrivateSet>

      {/* Manager/Teacher routes - AdminContainerLayout for Dojo */}
      <PrivateSet unauthenticated="login" roles={['Manager', 'Teacher']} wrap={AdminContainerLayout}>
        <Route path="/dojo/admin" page={DojoQuestionAdminPage} name="dojoAdmin" />
        <Route path="/dojo/results/{tasker:String}" page={DojoQuestionResultsPage} name="dojoResultsByTasker" />
      </PrivateSet>

      {/* Manager/Teacher routes - AdminDashboardLayout */}
      <PrivateSet unauthenticated="login" roles={['Manager', 'Teacher']} wrap={AdminDashboardLayout}>
        <Route path="/my-reports/{userId:Int}" page={MyReportsPage} name="myReportsByUserId" />
        <Route path="/my-conversations/{tasker:String}" page={MyConversationsPage} name="myConversationsByTasker" />
      </PrivateSet>

      {/* Self-serve application sub-routes - require auth */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Super']} wrap={PublicLayoutMd}>
        <Route path="/apply/{shortCode:String}/change-unit" page={ChangeUnitPage} name="changeUnit" />
      </PrivateSet>

      {/* Unit info and apply entry routes — full-width split-screen layout */}
      <Set wrap={PublicLayoutFull}>
        <Route path="/apply" page={ApplyLandingPage} name="applyLanding" />
        <Route path="/apply-to" page={ApplyLandingPage} name="applyTo" />
        <Route path="/rentcafe-fake" page={RentCafeFakePage} name="rentCafeFake" />
        <Route path="/rentcafe-apply" page={RentCafeApplyPage} name="rentCafeApply" />
      </Set>

      <PrivateSet unauthenticated="login" roles={['Agent', 'Admin', 'Super']}>
        <Route path="/home" page={HomePage} name="home" />
        <Route path="/evie-home" page={HomePage} name="evieHome" />
      </PrivateSet>

      <Set wrap={PublicLayoutMd}>
        <Route path="/guides/ssn-correction-yardi" page={YardiSsnCorrectionGuidePage} name="yardiSsnCorrectionGuide" />
        <Route path="/" page={LoginPage} name="login" />
        {/* Shareable SSO sign-in link for PM staff whose employer's mail server
          blocks our one-time-code emails. Same page as "/" — the path itself is
          the signal: src/auth.tsx pins the browser to Auth0 (and its "Continue
          with Microsoft" button) when it sees /login-user. */}
        <Route path="/login-user" page={LoginPage} name="loginUser" />
        <Route path="/inv/{shortCode:String}" page={InviteRedirectPage} name="inviteRedirect" />
      </Set>

      {/* Agent2 dev harness. Wrapped because the page reads
        theme.designTokens in render and no theme is mounted above a
        route — every layout brings its own document shell. */}
      <Set wrap={ClientContainerLayout}>
        <Route path="/process-tracker-dev" page={ProcessTrackerDevPage} name="processTrackerDev" />
      </Set>

      {/* notfound route doesn't respect Set wrappers, so NotFoundPage wraps itself with PublicLayout */}
      <Route notfound page={NotFoundPage} />

      {/* Shared applicant session lifecycle has no visual shell. ApplicantPage
        selects sibling Eve/Evie pages after enrollment resolves; direct apply
        goes straight to Eve. Each product owns its shell. Keep light-only paths
        in index.html in step. */}
      <PrivateSet unauthenticated="login" roles={['Applicant', 'Super']} wrap={ApplicantSessionLayout}>
        <Route path="/apply/{shortCode:String}" page={EvePage} name="selfServeApply" />
        <Route path="/eve/{workflowId:Int}/applicant/{applicantId:Int}/fake-phone" page={EvieFakePhonePage} name="evieFakePhone" />
        <Route path="/eve/{workflowId:Int}/applicant/{applicantId:Int}" page={ApplicantPage} name="eveApplicant" />
      </PrivateSet>
    </Router>
  )
}

export default Routes
